import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import businessConfig from "../../config/business";
import staticMenu from "../../data/menu";
import whiteWinePhoto from "../../assets/images/stock/white-wine.jpg";
import redWinePhoto from "../../assets/images/stock/red-wine.jpg";

// How long a guest waits on the server before the bundled menu is shown instead.
export const API_TIMEOUT_MS = 8000;

// Ids are compared as strings so a number in the static file and a string from the
// API both match what is listed in the config.
const asKeys = (ids) => new Set((ids || []).map(String));
const hiddenItems = asKeys(businessConfig.hiddenItemIds);
const hiddenCategories = asKeys(businessConfig.hiddenCategoryIds);

/**
 * Drop anything the config hides.
 *
 * Applied here, as the menu enters the store, so every consumer honours it - the
 * home rows, the menu grid, search, and ProductDetailPage, which reads the store
 * directly and would otherwise still serve a hidden dish by URL.
 */
const applyVisibility = (categories) => {
    if (!Array.isArray(categories)) return categories;
    if (!hiddenItems.size && !hiddenCategories.size) return categories;

    return categories
        .filter((category) => !hiddenCategories.has(String(category?.id)))
        .map((category) => ({
            ...category,
            menuItems: (category?.menuItems || []).filter((item) => !hiddenItems.has(String(item?.id))),
        }))
        // A category whose dishes are all hidden has nothing left to show.
        .filter((category) => category.menuItems.length > 0);
};

const trimmed = (value) => (typeof value === "string" ? value.trim() : "");

/**
 * Whether a dish or a category the server sent is one to put in front of a guest.
 *
 * The admin switches a dish off when the kitchen runs out of it, and the server sends it anyway -
 * it has to, because the admin reads the same endpoint and has to keep seeing what it switched
 * off. Leaving it off the menu is this app's job.
 *
 * Anything other than INACTIVE counts as on, so a status the server could not read (it sends null
 * then) leaves the dish on the menu rather than making it disappear for a reason no one can see.
 */
const isActive = (status) => String(status ?? "").trim().toUpperCase() !== "INACTIVE";

// The server holds only the kitchen's own photos, and it has none of the wines. The two
// wine lists borrow the stock glasses src/data/menu.js uses - labelled, as there, as a
// reference image. A photo uploaded for a wine in the admin takes their place.
const STAND_IN_PHOTOS = {
    "Verë e Bardhë": whiteWinePhoto,
    "Verë e Kuqe": redWinePhoto,
};

const standInPhoto = (item, category) => {
    const photo = STAND_IN_PHOTOS[category?.categoryName];
    return photo && !trimmed(item?.imageUrl) ? { imageUrl: photo, isReferenceImage: true } : {};
};

/**
 * The server's menu in the shape of src/data/menu.js, which is what every component reads.
 *
 * The two already match, bar what the server has no field for:
 *   note       <- categoryDescription   the line under a category heading
 *   priceUnit  <- size                  "për kg", "2 pers." - enter it as the dish's size
 *   icon       <- categoryType          drinks get a glass; everything else keeps the
 *                                       placeholder's fish, which would read oddly on a wine
 *   imageUrl   <- STAND_IN_PHOTOS       for a wine with no photo of its own
 * An empty description comes back as a single space, and a category with no dishes yet
 * has nothing to show. Neither has one whose dishes are all switched off in the admin.
 */
const fromApi = (categories) =>
    (Array.isArray(categories) ? categories : [])
        .filter((category) => isActive(category?.menuCategoryStatus))
        .map((category) => ({
            ...category,
            note: trimmed(category?.categoryDescription),
            menuItems: (category?.menuItems || [])
                .filter((item) => isActive(item?.menuItemStatus))
                .map((item) => ({
                    ...item,
                    description: trimmed(item?.description),
                    priceUnit: trimmed(item?.size),
                    icon: category?.categoryType === "BEVERAGE" ? "🍷" : undefined,
                    ...standInPhoto(item, category),
                })),
        }))
        .filter((category) => category.menuItems.length > 0);

// Loads the menu from the server, the way the Hira and Vivas apps do. src/data/menu.js
// is the backup: it is what the thunk returns while `menuSource` is "static" or there is
// no `businessId`, and what the reducers fall back to when the server fails, is too slow
// or has no dishes. Settings are read on every call, not at import, so tests can vary them.
export const fetchProductsFromApi = createAsyncThunk(
    'products/fetchFromApi',
    async (_, { rejectWithValue }) => {
        const { menuSource, businessId, api } = businessConfig;
        if (menuSource !== 'api' || !businessId) {
            return staticMenu;
        }

        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

        try {
            const response = await fetch(`${api.url}/api/menu-item/business/${businessId}`, {
                signal: controller.signal,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({})); // Catch in case response is not JSON
                const errorMessage = errorData?.message || `Failed to fetch products (HTTP ${response.status})`;
                throw new Error(errorMessage);
            }
            const text = await response.text();

            try {
                return fromApi(JSON.parse(text));
            } catch (parseError) {
                throw new Error('Invalid JSON received from server');
            }
        } catch (err) {
            return rejectWithValue(err.message);
        } finally {
            clearTimeout(timer);
        }
    }
);

const initialState = {
    products: [],
    status: 'idle',
    error: null,
    // True while the menu on screen is src/data/menu.js standing in for the server's.
    usedFallback: false,
};

const productsSlice = createSlice({
    name: 'products',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchProductsFromApi.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchProductsFromApi.fulfilled, (state, action) => {
                // A menu with no dishes is no more use to a guest than a failed request.
                const hasMenu = Array.isArray(action.payload) && action.payload.length > 0;
                state.status = 'succeeded';
                state.products = applyVisibility(hasMenu ? action.payload : staticMenu);
                state.usedFallback = !hasMenu;
                state.error = null;
            })
            .addCase(fetchProductsFromApi.rejected, (state, action) => {
                // The status stays honest, but the guest at the table gets the bundled
                // menu rather than an empty page.
                state.status = 'failed';
                state.products = applyVisibility(staticMenu);
                state.usedFallback = true;
                // @ts-ignore
                state.error = action.payload;
            });

    },

});


export default productsSlice.reducer;


export const selectAllProducts = (state) => state.products.products;
export const selectProductsStatus = (state) => state.products.status;
export const selectProductsError = (state) => state.products.error;
